import { useTransitionGuards } from "./useTransitionGuards";
import type { Match } from "../types";
import type { PeriodPhase } from "./usePeriodManager";

interface UseCockpitTransitionStateParams {
  statusOverride?: Match["status"];
  currentPhase: PeriodPhase;
  globalTimeSeconds: number;
  match: Match | null;
  isAdmin: boolean;
  t: (key: string, fallback: string) => string;
}

export const useCockpitTransitionState = ({
  statusOverride,
  currentPhase,
  globalTimeSeconds,
  match,
  isAdmin,
  t,
}: UseCockpitTransitionStateParams) => {
  const transitionGuards = useTransitionGuards({
    statusOverride,
    currentPhase,
    globalTimeSeconds,
    match,
    t,
  });

  const cockpitLocked = currentPhase === "COMPLETED";
  const lockReason = cockpitLocked
    ? t("lockReasonFulltime", "Match is Fulltime. Cockpit is read-only.")
    : undefined;

  const transitionDisabled =
    cockpitLocked ||
    !isAdmin ||
    (currentPhase === "Q1"
      ? !transitionGuards.canBreakQ1 || !transitionGuards.hasQ1Minimum
      : currentPhase === "BREAK_Q1"
        ? !transitionGuards.canQ2
        : currentPhase === "Q2"
          ? !transitionGuards.canHalftime || !transitionGuards.hasQ2Minimum
          : currentPhase === "HALFTIME"
            ? !transitionGuards.canQ3
            : currentPhase === "Q3"
              ? !transitionGuards.canBreakQ3 || !transitionGuards.hasQ3Minimum
              : currentPhase === "BREAK_Q3"
                ? !transitionGuards.canQ4
                : currentPhase === "Q4"
                  ? !transitionGuards.canFulltime || !transitionGuards.hasQ4Minimum
                  : currentPhase === "OVERTIME"
                    ? !transitionGuards.hasOTMinimum
                    : false);

  const transitionReason = cockpitLocked
    ? lockReason
    : !isAdmin
      ? t(
          "adminOnlyTransitions",
          "Admin only: match status changes are locked.",
        )
      : currentPhase === "Q1" &&
          (!transitionGuards.canBreakQ1 ||
            !transitionGuards.hasQ1Minimum)
        ? !transitionGuards.hasQ1Minimum
          ? transitionGuards.minimumQ1Reason
          : transitionGuards.transitionGuardMessage
        : currentPhase === "BREAK_Q1" && !transitionGuards.canQ2
          ? transitionGuards.transitionGuardMessage
          : currentPhase === "Q2" &&
              (!transitionGuards.canHalftime ||
                !transitionGuards.hasQ2Minimum)
            ? !transitionGuards.hasQ2Minimum
              ? transitionGuards.minimumQ2Reason
              : transitionGuards.transitionGuardMessage
            : currentPhase === "HALFTIME" && !transitionGuards.canQ3
              ? transitionGuards.transitionGuardMessage
              : currentPhase === "Q3" &&
                  (!transitionGuards.canBreakQ3 ||
                    !transitionGuards.hasQ3Minimum)
                ? !transitionGuards.hasQ3Minimum
                  ? transitionGuards.minimumQ3Reason
                  : transitionGuards.transitionGuardMessage
                : currentPhase === "BREAK_Q3" && !transitionGuards.canQ4
                  ? transitionGuards.transitionGuardMessage
                  : currentPhase === "Q4" &&
                      (!transitionGuards.canFulltime ||
                        !transitionGuards.hasQ4Minimum)
                    ? !transitionGuards.hasQ4Minimum
                      ? transitionGuards.minimumQ4Reason
                      : transitionGuards.transitionGuardMessage
                    : currentPhase === "OVERTIME" &&
                        !transitionGuards.hasOTMinimum
                      ? transitionGuards.minimumOTReason
                      : undefined;

  return {
    ...transitionGuards,
    cockpitLocked,
    lockReason,
    transitionDisabled,
    transitionReason,
  };
};
